// ABOUTME: Hypothesis detail page loading analysis via React Query
// ABOUTME: Replaces App.tsx hypothesis loading logic

import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Pencil } from 'lucide-react';
import { useHypothesisAnalysis } from '@/hooks/useHypotheses';
import { HypothesisView } from '@/components/hypothesis/HypothesisView';
import { EditHypothesisDialog } from '@/components/hypothesis/EditHypothesisDialog';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';

export const HypothesisPage: React.FC = () => {
  const { category, id } = useParams<{ category: string; id: string }>();
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'leads';
  const [editOpen, setEditOpen] = useState(false);

  const { data, isLoading, error } = useHypothesisAnalysis(category!, id!);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading analysis data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="m-8 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        <strong>Error:</strong> Failed to load hypothesis data. {(error as any).response?.data?.detail || (error as Error).message}
      </div>
    );
  }

  if (!data) {
    return null;
  }

  // Extract just the H-code (e.g., "H10" from "alx-brand-perception_H10")
  const hypothesisCode = data.hypothesis_id.split('_').pop() || data.hypothesis_id;

  return (
    <>
      {canEdit && (
        <div className="flex justify-end px-8 pt-4">
          <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
            <Pencil className="w-4 h-4 mr-1" /> Edit hypothesis
          </Button>
        </div>
      )}
      {canEdit && (
        <EditHypothesisDialog
          hypothesisId={data.hypothesis_id}
          open={editOpen}
          onClose={() => setEditOpen(false)}
        />
      )}
      <HypothesisView
        data={data}
        hypothesisName={hypothesisCode}
      />
    </>
  );
};
